// src/App.js
import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";

import LandingTab from "./components/landing/LandingTab";
import ProductsTab from "./components/products/ProductsTab";
import MarketsTab from "./components/markets/MarketsTab";

import CenteredLogo from "./components/layout/CenteredLogo";
import TransitionOverlay from "./components/layout/TransitionOverlay";
import TopBar from "./components/layout/TopBar";

import "./App.css";

export default function App() {
  // PERSISTED STATE -----------------------------------
  const [entered, setEntered] = useState(
    () => localStorage.getItem("app.entered") === "1"
  );
  const [tab, setTab] = useState(
    () => localStorage.getItem("app.tab") || "landing"
  );

  useEffect(() => {
    localStorage.setItem("app.entered", entered ? "1" : "0");
  }, [entered]);

  useEffect(() => {
    localStorage.setItem("app.tab", tab);
  }, [tab]);

  // OVERLAY -------------------------------------------
  const [overlayStage, setOverlayStage] = useState("off");
  const isTransitioning = useRef(false);

  const DURATIONS = useMemo(() => ({ in: 420, hold: 140, out: 700 }), []);

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  useEffect(() => {
    const img = new Image();
    img.src = `${process.env.PUBLIC_URL}/Icon.svg`;
  }, []);

  // first fade
  useEffect(() => {
    (async () => {
      setOverlayStage("out");
      await sleep(DURATIONS.out);
      setOverlayStage("off");
    })();
  }, [DURATIONS]);

  // ENTER ANIMATION -----------------------------------
  const handleEnter = useCallback(async () => {
    if (isTransitioning.current) return;
    isTransitioning.current = true;

    setOverlayStage("in");
    await sleep(DURATIONS.in + DURATIONS.hold);

    setEntered(true);
    setTab("landing");

    setOverlayStage("out");
    await sleep(DURATIONS.out);
    setOverlayStage("off");

    isTransitioning.current = false;
  }, [DURATIONS]);

  // TAB SWITCH ----------------------------------------
  const handleTabSwitch = useCallback(
    async (next) => {
      if (next === tab) return;
      if (isTransitioning.current) return;

      isTransitioning.current = true;

      setOverlayStage("in");
      await sleep(DURATIONS.in + DURATIONS.hold);

      setTab(next);

      setOverlayStage("out");
      await sleep(DURATIONS.out);
      setOverlayStage("off");

      isTransitioning.current = false;
    },
    [tab, DURATIONS]
  );

  // KEYBOARD SHORTCUTS --------------------------------
  useEffect(() => {
    const onKey = (e) => {
      if (!entered) {
        if (e.key === "Enter" || e.key === " ") handleEnter();
        return;
      }

      if (e.key === "0") handleTabSwitch("landing");
      if (e.key === "1") handleTabSwitch("markets");
      if (e.key === "2") handleTabSwitch("products");
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [entered, handleEnter, handleTabSwitch]);

  // LANDING GATE --------------------------------------
  if (!entered) {
    return (
      <div className="landing">
        <CenteredLogo label="Alkeme UX" />

        <div className="cta">
          <button className="enter-btn" onClick={handleEnter} autoFocus>
            Enter Alkeme
          </button>
          <p className="hint">
            Click the button or press <b>Enter</b>
          </p>
        </div>

        <div className="tap-area" onClick={handleEnter} />
        <TransitionOverlay stage={overlayStage} />
      </div>
    );
  }

  // MAIN APP ------------------------------------------
  return (
    <div className="app-shell">
      <TransitionOverlay stage={overlayStage} />

      <TopBar tab={tab} onSwitch={handleTabSwitch} />

      <main className="content">
        {tab === "landing" && (
          <section className="stack gap-6 fade-in">
            <LandingTab />
          </section>
        )}

        {tab === "markets" && (
          <section className="stack gap-6 fade-in">
            <MarketsTab />
          </section>
        )}

        {tab === "products" && (
          <section className="stack gap-6 fade-in">
            <ProductsTab />
          </section>
        )}
      </main>

      <footer className="footer">
        <span>© {new Date().getFullYear()} Alkeme UX</span>
        <span className="muted">Use the tabs to navigate.</span>
      </footer>
    </div>
  );
}
